import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  SectionHeader,
} from "@babackapp/customer-ui/components/header";
import { OptionsList } from "@babackapp/customer-ui/components/option";
import { RadioGroup } from "@babackapp/customer-ui/components/radio-group";
import {
  ReceiptLine,
  ReceiptLineLabel,
  ReceiptLineValue,
} from "@babackapp/customer-ui/components/receipt-line";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import { Alert, AlertDescription } from "@babackapp/ui/components/alert";
import { Separator } from "@babackapp/ui/components/separator";
import { EnterIcon } from "@radix-ui/react-icons";
import { ShoppingBagIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCheckout } from "../hooks/use-checkout";
import { useStripeCheckout } from "../hooks/use-stripe-checkout";
import { useWidgetParams } from "../hooks/use-widget-params";
import { ShippingRateOption } from "./shipping-rate-option";
import { StripePaymentElement } from "./stripe-payment-element";

export function Checkout() {
  const navigate = useNavigate();
  const { orderId, shopId } = useWidgetParams();
  const {
    financialSummary,
    shippingRates,
    selectedRateId,
    selectRate,
    canProceed,
    onProceed,
    isLoadingRates,
    checkoutToken,
  } = useCheckout();

  const needsPayment = financialSummary.amountDue > 0;
  const hasExchangeCredit = financialSummary.exchangeTotal > 0;

  const stripe = useStripeCheckout(orderId, shopId, checkoutToken);

  const handleGoShopping = () => navigate("../shop-now");

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>Paiement</HeaderTitle>
        <HeaderDescription>
          {needsPayment
            ? "Pour confirmer votre retour, veuillez payer les frais supplémentaires."
            : "Vérifiez le récapitulatif de votre retour avant de confirmer."}
        </HeaderDescription>
      </Header>

      <div className="w-full mt-4 flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          {financialSummary.refundTotal > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>Total à rembourser</ReceiptLineLabel>
              <ReceiptLineValue>
                {formatPrice(financialSummary.refundTotal, financialSummary.currency)}
              </ReceiptLineValue>
            </ReceiptLine>
          )}
          {financialSummary.exchangeTotal > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>Total échange</ReceiptLineLabel>
              <ReceiptLineValue>
                {formatPrice(financialSummary.exchangeTotal, financialSummary.currency)}
              </ReceiptLineValue>
            </ReceiptLine>
          )}
          {financialSummary.discountTotal > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>Bon de réduction</ReceiptLineLabel>
              <ReceiptLineValue>
                {formatPrice(financialSummary.discountTotal, financialSummary.currency)}
              </ReceiptLineValue>
            </ReceiptLine>
          )}
          {needsPayment && (
            <>
              <Separator className="my-1" />
              <ReceiptLine>
                <ReceiptLineLabel className="font-medium text-foreground">
                  Montant dû
                </ReceiptLineLabel>
                <ReceiptLineValue className="font-medium text-foreground">
                  {formatPrice(financialSummary.amountDue, financialSummary.currency)}
                </ReceiptLineValue>
              </ReceiptLine>
            </>
          )}
        </div>

        {needsPayment && (
          <>
            {shippingRates.length > 0 && (
              <div className="flex flex-col gap-3">
                <SectionHeader>Choisissez un mode de livraison</SectionHeader>
                {isLoadingRates && (
                  <p className="text-sm text-muted-foreground">
                    Chargement des options de livraison…
                  </p>
                )}
                {!isLoadingRates && (
                  <OptionsList>
                    <RadioGroup value={selectedRateId ?? ""} onValueChange={selectRate}>
                      {shippingRates.map((rate) => (
                        <ShippingRateOption
                          key={rate.handle}
                          rate={rate}
                          currency={financialSummary.currency}
                        />
                      ))}
                    </RadioGroup>
                  </OptionsList>
                )}
              </div>
            )}

            <StripePaymentElement
              stripePromise={stripe.stripePromise}
              options={stripe.elementsOptions}
              onReady={stripe.onReady}
              onLoadError={stripe.onLoadError}
            />

            {stripe.errorMessage && (
              <Alert variant="destructive">
                <AlertDescription>{stripe.errorMessage}</AlertDescription>
              </Alert>
            )}
          </>
        )}
      </div>

      <AnimatedButtonRow variants={{}}>
        {hasExchangeCredit ? (
          <AnimatedButtonContainer layoutId="shop-now-button" variants={{}}>
            <Button onClick={handleGoShopping}>
              <ShoppingBagIcon />
              Choisir mes articles
            </Button>
          </AnimatedButtonContainer>
        ) : needsPayment ? (
          <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
            <Button
              disabled={!canProceed || !stripe.isReady || stripe.isSubmitting}
              onClick={stripe.onSubmit}
            >
              <EnterIcon />
              {stripe.isSubmitting
                ? "Paiement en cours…"
                : `Payer ${formatPrice(financialSummary.amountDue, financialSummary.currency)}`}
            </Button>
          </AnimatedButtonContainer>
        ) : (
          <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
            <Button disabled={!canProceed} onClick={onProceed}>
              <EnterIcon />
              Confirmer
            </Button>
          </AnimatedButtonContainer>
        )}
      </AnimatedButtonRow>
    </WidgetPage>
  );
}
